import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './MyAds.css';

const MyAds = () => {
    const [ads, setAds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [showEditForm, setShowEditForm] = useState(false);
    const [selectedAd, setSelectedAd] = useState(null);
    const [backendType, setBackendType] = useState('flask'); // 'flask' or 'django'
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [showLoginForm, setShowLoginForm] = useState(false);
    const [loginData, setLoginData] = useState({ email: '', password: '' });
    
    // Form state
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        image_url: '',
        needs_design_help: false,
        creative_brief: ''
    });

    // API endpoints based on backend type
    const getApiEndpoints = () => {
        if (backendType === 'django') {
            return {
                baseUrl: '', // Use relative URLs
                ads: '/api/ads/',
                createAd: '/api/ads/create/',
                updateAd: (id) => `/api/ads/${id}/update/`,
                deleteAd: (id) => `/api/ads/${id}/delete/`,
                clientAds: '/api/client/ads/'
            };
        } else {
            return {
                baseUrl: '', // Use relative URLs
                ads: '/api/client/ads',
                createAd: '/api/ads',
                updateAd: (id) => `/api/ads/${id}`,
                deleteAd: (id) => `/api/ads/${id}`,
                clientAds: '/api/client/ads'
            };
        }
    };

    useEffect(() => {
        fetchAds();
    }, [backendType]);

    const fetchAds = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const endpoints = getApiEndpoints();
            
            const response = await axios.get(`${endpoints.baseUrl}${endpoints.clientAds}`, {
                withCredentials: true // This is important for session cookies
            });
            
            setAds(response.data);
        } catch (err) {
            console.error('Error fetching ads:', err);
            if (err.response && err.response.status === 401) {
                setError('Please log in to view your ads.');
                setIsLoggedIn(false);
            } else {
                setError('Failed to fetch ads. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        console.log('Login attempt with:', loginData);
        
        try {
            const endpoints = getApiEndpoints();
            console.log('Using endpoints:', endpoints);
            console.log('Making request to:', `${endpoints.baseUrl}/api/auth/login`);
            
            const response = await axios.post(`${endpoints.baseUrl}/api/auth/login`, loginData, {
                withCredentials: true
            });
            
            console.log('Login response:', response);
            
            if (response.status === 200) {
                console.log('Login successful!');
                setIsLoggedIn(true);
                setShowLoginForm(false);
                setError(null);
                fetchAds(); // Fetch ads after successful login
            }
        } catch (err) {
            console.error('Login error:', err);
            console.error('Error response:', err.response);
            setError('Login failed. Please check your credentials.');
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        console.log('Register attempt with:', loginData);
        
        try {
            const endpoints = getApiEndpoints();
            const registerData = {
                ...loginData,
                name: loginData.email.split('@')[0], // Use email prefix as name
                company_name: 'Test Company'
            };
            console.log('Register data:', registerData);
            console.log('Making request to:', `${endpoints.baseUrl}/api/auth/register`);
            
            const response = await axios.post(`${endpoints.baseUrl}/api/auth/register`, registerData, {
                withCredentials: true
            });
            
            console.log('Register response:', response);
            
            if (response.status === 201) {
                console.log('Registration successful!');
                setIsLoggedIn(true);
                setShowLoginForm(false);
                setError(null);
                fetchAds(); // Fetch ads after successful registration
            }
        } catch (err) {
            console.error('Registration error:', err);
            console.error('Error response:', err.response);
            setError('Registration failed. Please try again.');
        }
    };

    const handleCreateAd = async (e) => {
        e.preventDefault();
        
        try {
            const endpoints = getApiEndpoints();
            
            const response = await axios.post(`${endpoints.baseUrl}${endpoints.createAd}`, formData, {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            if (response.status === 201 || response.status === 200) {
                alert('Advertisement created successfully!');
                setShowCreateForm(false);
                resetForm();
                fetchAds();
            }
        } catch (err) {
            console.error('Error creating ad:', err);
            alert('Failed to create advertisement. Please try again.');
        }
    };

    const handleUpdateAd = async (e) => {
        e.preventDefault();
        
        try {
            const endpoints = getApiEndpoints();
            
            const response = await axios.put(
                `${endpoints.baseUrl}${endpoints.updateAd(selectedAd.id)}`, 
                formData,
                {
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );
            
            if (response.status === 200) {
                alert('Advertisement updated successfully!');
                setShowEditForm(false);
                setSelectedAd(null);
                resetForm();
                fetchAds();
            }
        } catch (err) {
            console.error('Error updating ad:', err);
            alert('Failed to update advertisement. Please try again.');
        }
    };

    const handleDeleteAd = async (adId) => {
        if (!window.confirm('Are you sure you want to delete this advertisement?')) {
            return;
        }
        
        try {
            const endpoints = getApiEndpoints();
            
            const response = await axios.delete(`${endpoints.baseUrl}${endpoints.deleteAd(adId)}`, {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            if (response.status === 200 || response.status === 204) {
                alert('Advertisement deleted successfully!');
                fetchAds();
            }
        } catch (err) {
            console.error('Error deleting ad:', err);
            alert('Failed to delete advertisement. Please try again.');
        }
    };

    const handleEditClick = (ad) => {
        setSelectedAd(ad);
        setFormData({
            title: ad.title,
            description: ad.description,
            image_url: ad.image_url || '',
            needs_design_help: ad.needs_design_help,
            creative_brief: ad.creative_brief || ''
        });
        setShowEditForm(true);
    };

    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            image_url: '',
            needs_design_help: false,
            creative_brief: ''
        });
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    if (loading) {
        return (
            <div className="my-ads-container">
                <div className="loading">Loading your advertisements...</div>
            </div>
        );
    }

    return (
        <div className="my-ads-container">
            <div className="my-ads-header">
                <h2>My Advertisements</h2>
                <div className="backend-selector">
                    <label>Backend: </label>
                    <select 
                        value={backendType} 
                        onChange={(e) => setBackendType(e.target.value)}
                    >
                        <option value="flask">Flask (Port 8000)</option>
                        <option value="django">Django (Port 8001)</option>
                    </select>
                </div>
                {isLoggedIn ? (
                    <button 
                        className="create-ad-btn"
                        onClick={() => setShowCreateForm(true)}
                    >
                        Create New Ad
                    </button>
                ) : (
                    <button 
                        className="create-ad-btn"
                        onClick={() => setShowLoginForm(true)}
                    >
                        Login to Create Ads
                    </button>
                )}
            </div>

            {error && (
                <div className="error-message">
                    {error}
                </div>
            )}

            {ads.length === 0 ? (
                <div className="no-ads">
                    <p>You haven't created any advertisements yet.</p>
                    <button 
                        className="create-first-ad-btn"
                        onClick={() => setShowCreateForm(true)}
                    >
                        Create Your First Ad
                    </button>
                </div>
            ) : (
                <div className="ads-grid">
                    {ads.map((ad) => (
                        <div key={ad.id} className="ad-card">
                            {ad.image_url && (
                                <div className="ad-image">
                                    <img src={ad.image_url} alt={ad.title} />
                                </div>
                            )}
                            <div className="ad-content">
                                <h3>{ad.title}</h3>
                                <p className="ad-description">{ad.description}</p>
                                <div className="ad-meta">
                                    <span className={`status status-${ad.status}`}>
                                        {ad.status}
                                    </span>
                                    {ad.needs_design_help && (
                                        <span className="design-help">Needs Design Help</span>
                                    )}
                                </div>
                                <div className="ad-actions">
                                    <button 
                                        className="edit-btn"
                                        onClick={() => handleEditClick(ad)}
                                    >
                                        Edit
                                    </button>
                                    <button 
                                        className="delete-btn"
                                        onClick={() => handleDeleteAd(ad.id)}
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create Ad Modal */}
            {showCreateForm && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        background: 'white',
                        borderRadius: '12px',
                        width: '90%',
                        maxWidth: '500px',
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '20px',
                            borderBottom: '1px solid #e0e0e0'
                        }}>
                            <h3 style={{margin: 0, color: '#333', fontSize: '1.3rem'}}>Create New Advertisement</h3>
                            <button 
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '24px',
                                    cursor: 'pointer',
                                    color: '#666',
                                    width: '30px',
                                    height: '30px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '50%'
                                }}
                                onClick={() => {
                                    setShowCreateForm(false);
                                    resetForm();
                                }}
                            >
                                ×
                            </button>
                        </div>
                        
                        <div style={{padding: '20px'}}>
                            <form onSubmit={handleCreateAd}>
                                <div style={{marginBottom: '20px'}}>
                                    <label style={{display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333'}}>
                                        Title *
                                    </label>
                                    <input
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        required
                                        style={{
                                            width: '100%',
                                            padding: '12px',
                                            border: '1px solid #ddd',
                                            borderRadius: '6px',
                                            fontSize: '14px',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                </div>
                                
                                <div style={{marginBottom: '20px'}}>
                                    <label style={{display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333'}}>
                                        Description
                                    </label>
                                    <textarea
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        rows="3"
                                        style={{
                                            width: '100%',
                                            padding: '12px',
                                            border: '1px solid #ddd',
                                            borderRadius: '6px',
                                            fontSize: '14px',
                                            boxSizing: 'border-box',
                                            resize: 'vertical'
                                        }}
                                    />
                                </div>
                                
                                <div style={{marginBottom: '20px'}}>
                                    <label style={{display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333'}}>
                                        Image URL
                                    </label>
                                    <input
                                        type="url"
                                        name="image_url"
                                        value={formData.image_url}
                                        onChange={handleInputChange}
                                        placeholder="https://example.com/image.jpg"
                                        style={{
                                            width: '100%',
                                            padding: '12px',
                                            border: '1px solid #ddd',
                                            borderRadius: '6px',
                                            fontSize: '14px',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                </div>
                                
                                <div style={{marginBottom: '20px'}}>
                                    <label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer'}}>
                                        <input
                                            type="checkbox"
                                            name="needs_design_help"
                                            checked={formData.needs_design_help}
                                            onChange={handleInputChange}
                                            style={{width: 'auto', margin: 0}}
                                        />
                                        <span style={{fontWeight: '500', color: '#333'}}>Need design help?</span>
                                    </label>
                                </div>
                                
                                {formData.needs_design_help && (
                                    <div style={{marginBottom: '20px'}}>
                                        <label style={{display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333'}}>
                                            Creative Brief
                                        </label>
                                        <textarea
                                            name="creative_brief"
                                            value={formData.creative_brief}
                                            onChange={handleInputChange}
                                            rows="4"
                                            placeholder="Describe your vision for the ad design..."
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                border: '1px solid #ddd',
                                                borderRadius: '6px',
                                                fontSize: '14px',
                                                boxSizing: 'border-box',
                                                resize: 'vertical'
                                            }}
                                        />
                                    </div>
                                )}
                                
                                <div style={{display: 'flex', gap: '10px', marginTop: '30px'}}>
                                    <button 
                                        type="submit" 
                                        style={{
                                            flex: 1,
                                            padding: '12px 24px',
                                            border: 'none',
                                            borderRadius: '6px',
                                            fontSize: '16px',
                                            fontWeight: '500',
                                            cursor: 'pointer',
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            color: 'white'
                                        }}
                                    >
                                        Create Advertisement
                                    </button>
                                    <button 
                                        type="button" 
                                        style={{
                                            flex: 1,
                                            padding: '12px 24px',
                                            border: 'none',
                                            borderRadius: '6px',
                                            fontSize: '16px',
                                            fontWeight: '500',
                                            cursor: 'pointer',
                                            background: '#6c757d',
                                            color: 'white'
                                        }}
                                        onClick={() => {
                                            setShowCreateForm(false);
                                            resetForm();
                                        }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Ad Modal */}
            {showEditForm && selectedAd && (
                <div className="modal-overlay">
                    <div className="modal">
                        <div className="modal-header">
                            <h3>Edit Advertisement</h3>
                            <button 
                                className="close-btn"
                                onClick={() => {
                                    setShowEditForm(false);
                                    setSelectedAd(null);
                                    resetForm();
                                }}
                            >
                                ×
                            </button>
                        </div>
                        <div className="modal-body">
                            <form onSubmit={handleUpdateAd} className="ad-form">
                                <div className="form-group">
                                    <label>Title *</label>
                                    <input
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Description</label>
                                    <textarea
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        rows="3"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Image URL</label>
                                    <input
                                        type="url"
                                        name="image_url"
                                        value={formData.image_url}
                                        onChange={handleInputChange}
                                        placeholder="https://example.com/image.jpg"
                                    />
                                </div>
                                <div className="form-group checkbox-group">
                                    <label>
                                        <input
                                            type="checkbox"
                                            name="needs_design_help"
                                            checked={formData.needs_design_help}
                                            onChange={handleInputChange}
                                        />
                                        Need design help?
                                    </label>
                                </div>
                                {formData.needs_design_help && (
                                    <div className="form-group">
                                        <label>Creative Brief</label>
                                        <textarea
                                            name="creative_brief"
                                            value={formData.creative_brief}
                                            onChange={handleInputChange}
                                            rows="4"
                                            placeholder="Describe your vision for the ad design..."
                                        />
                                    </div>
                                )}
                                <div className="form-actions">
                                    <button type="submit" className="submit-btn">
                                        Update Advertisement
                                    </button>
                                    <button 
                                        type="button" 
                                        className="cancel-btn"
                                        onClick={() => {
                                            setShowEditForm(false);
                                            setSelectedAd(null);
                                            resetForm();
                                        }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Login Modal */}
            {showLoginForm && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        background: 'white',
                        borderRadius: '12px',
                        width: '90%',
                        maxWidth: '400px',
                        padding: '20px',
                        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '20px',
                            borderBottom: '1px solid #e0e0e0',
                            paddingBottom: '10px'
                        }}>
                            <h3 style={{margin: 0, color: '#333'}}>Login / Register</h3>
                            <button 
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '24px',
                                    cursor: 'pointer',
                                    color: '#666'
                                }}
                                onClick={() => setShowLoginForm(false)}
                            >
                                ×
                            </button>
                        </div>
                        
                        <form onSubmit={handleLogin}>
                            <div style={{marginBottom: '15px'}}>
                                <label style={{display: 'block', marginBottom: '5px', fontWeight: '500'}}>
                                    Email:
                                </label>
                                <input
                                    type="email"
                                    value={loginData.email}
                                    onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>
                            
                            <div style={{marginBottom: '20px'}}>
                                <label style={{display: 'block', marginBottom: '5px', fontWeight: '500'}}>
                                    Password:
                                </label>
                                <input
                                    type="password"
                                    value={loginData.password}
                                    onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>
                            
                            <div style={{display: 'flex', gap: '10px'}}>
                                <button 
                                    type="submit" 
                                    style={{
                                        flex: 1,
                                        padding: '10px',
                                        background: '#007bff',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Login
                                </button>
                                <button 
                                    type="button" 
                                    onClick={handleRegister}
                                    style={{
                                        flex: 1,
                                        padding: '10px',
                                        background: '#28a745',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Register
                                </button>
                            </div>
                        </form>
                        
                        <div style={{marginTop: '15px', fontSize: '14px', color: '#666'}}>
                            <p>Use any email and password to register a new account, or login with existing credentials.</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MyAds;
